import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { Button, Chip, Input, Label, Switch, Table, TextField, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { Notice } from '../admin/settingsKit'
import { DAYS } from '../layout/media'
import { api, type BranchSeat, type Campaign, type PlaylistSummary, type SignageNow, type SignageScreen } from '../lib/api'
import { addDays, clockOfMinutes, colourOf, hourRange, weekOf, weekStartOf, type Block } from '../lib/signageCalendar'
import {
  PLACE_KEYS,
  PLACES,
  STATE_KEYS,
  bodyOf,
  branchNote,
  branchZone,
  campaignDayKeys,
  campaignHours,
  campaignProblems,
  changeMoment,
  formOf,
  nowBranch,
  type CampaignForm,
} from '../lib/signageEdit'
import { branchProblem, mosaicOf, type BranchProblem } from '../lib/signageViews'
import { explain } from '../lib/staffErrors'
import type { StaffKey, StaffLanguage } from '../lib/staffText'
import { Icon } from '../ui/icons'
import { MultiSelect } from '../ui/MultiSelect'
import { useStaffText } from '../ui/StaffText'
import { showToast } from '../ui/toast'
import type { SignageData } from './signageData'
import { ClockField, ConfirmModal, DayField, Empty, PickOne, SignageDrawer, TileSkeletons } from './signageKit'
import { Thumb } from './signageLibrary'

/** A YYYY-MM-DD as "9 Oct", the calendar day itself whatever this browser's zone. */
function calendarDay(day: string, lang: StaffLanguage, year = false): string {
  if (!day) return '—'
  return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short', ...(year ? { year: 'numeric' } : {}), timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`))
}

/** How a state reads as a chip: running is live, the ones that stop a campaign showing warn. */
const STATE_TONE: Partial<Record<NonNullable<Campaign['state']>, 'success' | 'warning'>> = {
  running: 'success',
  noSeat: 'warning',
  empty: 'warning',
  outranked: 'warning',
}

/** Branches shown in a row before "+n". */
const ROW_BRANCHES = 2

type View = 'list' | 'week'
const VIEW_KEY = 'qms.signage.campaignView'
const storedView = (): View => {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'week' ? 'week' : 'list'
  } catch {
    return 'list'
  }
}

const hasItems = (playlists: readonly PlaylistSummary[]) => (id: string) => {
  const p = playlists.find((x) => x.id === id)
  return !!p && (p.items > 0 || p.ticker.length > 0)
}

/** A branch's wall clock now: its calendar day and minute of the day. */
function wallNow(zone: string): { day: string; minute: number } {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(new Date())
      .map((p) => [p.type, p.value]),
  )
  return { day: `${parts.year}-${parts.month}-${parts.day}`, minute: Number(parts.hour) * 60 + Number(parts.minute) }
}

/** Why a branch shows nothing, with the way to fix it when there is one. */
function ProblemLine({ name, problem, onNavigate }: { name?: string; problem: BranchProblem; onNavigate: (to: string) => void }) {
  const { say } = useStaffText()
  return (
    <span className="signage-warn">
      <Icon name="alert" size={13} />
      <span>
        {name ? `${name}: ` : ''}
        {say(problem.key)}
      </span>
      {problem.key === 'signage.noArea' && (
        <button type="button" className="signage-link" onClick={() => onNavigate(problem.designId ? `/screens#${problem.designId}` : '/screens')}>
          {say('signage.openDesign')}
        </button>
      )}
    </span>
  )
}

/** Campaigns: a list or a week per branch, one being edited in a drawer. */
export function CampaignsTab({ data, onNavigate }: { data: SignageData; onNavigate: (to: string) => void }) {
  const { say, lang } = useStaffText()
  const [view, setView] = useState<View>(storedView)
  const [editing, setEditing] = useState<{ id?: string; form: CampaignForm } | null>(null)
  const [fault, setFault] = useState('')
  const [busy, setBusy] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const { campaigns, branches, playlists, today } = data
  const screens = data.loaded.screens && !data.faults.screens ? data.screens : null

  const chooseView = (v: View) => {
    setView(v)
    try {
      window.localStorage.setItem(VIEW_KEY, v)
    } catch {
      // Kept for this visit only.
    }
  }
  const edit = (next: { id?: string; form: CampaignForm } | null) => {
    setFault('')
    setDeleting(false)
    setEditing(next)
  }
  const run = async (fn: () => Promise<unknown>, doneKey: StaffKey) => {
    setBusy(true)
    setFault('')
    try {
      await fn()
      setEditing(null)
      setDeleting(false)
      showToast(say(doneKey))
      await data.reload('campaigns', 'playlists', 'screens')
    } catch (err) {
      setFault(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }
  const save = () => {
    if (!editing) return
    const stored = editing.id ? campaigns.find((c) => c.id === editing.id) : undefined
    const problems = campaignProblems(editing.form, today, stored?.endDate)
    if (problems.length > 0) {
      setFault(problems.map((k) => say(k)).join(' '))
      return
    }
    const body = bodyOf(editing.form)
    void run(() => (editing.id ? api.saveCampaign(editing.id, body) : api.createCampaign(body)), 'common.saved')
  }
  const open = (c: Campaign) => edit({ id: c.id, form: formOf(c, today) })
  const playlistOf = (id: string) => playlists.find((p) => p.id === id)
  const newButton = (
    <Button variant="primary" size="sm" isDisabled={!data.loaded.campaigns} onPress={() => edit({ form: formOf(undefined, today) })}>
      <Icon name="plus" size={16} /> {say('signage.newCampaign')}
    </Button>
  )
  const editingName = editing?.id ? campaigns.find((c) => c.id === editing.id)?.name : undefined

  return (
    <section className="signage-camps" aria-label={say('signage.tab.campaigns')}>
      {campaigns.length > 0 && (
        <div className="signage-bar">
          <ToggleButtonGroup
            className="signage-seg"
            size="sm"
            selectionMode="single"
            disallowEmptySelection
            aria-label={say('signage.view')}
            selectedKeys={[view]}
            onSelectionChange={(keys) => chooseView(([...keys][0] as View) ?? 'list')}
          >
            <ToggleButton id="list">
              <Icon name="table" size={15} /> {say('signage.view.list')}
            </ToggleButton>
            <ToggleButton id="week">
              <Icon name="grid" size={15} /> {say('signage.view.week')}
            </ToggleButton>
          </ToggleButtonGroup>
          <span className="signage-count">{say('signage.nCampaigns', { n: campaigns.length })}</span>
          <span className="signage-bar__gap" />
          {newButton}
        </div>
      )}
      {data.faults.campaigns && <Notice>{data.faults.campaigns}</Notice>}
      {!data.loaded.campaigns ? (
        <TileSkeletons n={3} className="signage-cards" />
      ) : campaigns.length === 0 ? (
        <div className="signage-dropzone">
          <Empty icon="clock" title={say('signage.campaigns.empty')} action={newButton} />
        </div>
      ) : view === 'week' ? (
        <WeekView campaigns={campaigns} branches={branches} playlists={playlists} onEdit={open} />
      ) : (
        <Table variant="secondary" className="signage-table">
          <Table.ScrollContainer>
            <Table.Content
              aria-label={say('signage.tab.campaigns')}
              onRowAction={(key) => {
                const c = campaigns.find((x) => x.id === key)
                if (c) open(c)
              }}
            >
              <Table.Header>
                <Table.Column isRowHeader>{say('signage.name')}</Table.Column>
                <Table.Column>{say('signage.state')}</Table.Column>
                <Table.Column>{say('signage.dates')}</Table.Column>
                <Table.Column>{say('signage.when')}</Table.Column>
                <Table.Column>{say('signage.branches')}</Table.Column>
                {PLACES.length > 1 ? <Table.Column>{say('signage.places')}</Table.Column> : null}
                <Table.Column>{say('signage.priority')}</Table.Column>
              </Table.Header>
              <Table.Body>
                {campaigns.map((c) => {
                  const chosen = branches.filter((b) => c.branchIds.includes(b.id))
                  const warned = chosen.map((b) => ({ b, problem: branchProblem(b, screens) })).filter((x): x is { b: BranchSeat; problem: BranchProblem } => !!x.problem)
                  const state = c.state ?? 'scheduled'
                  const tone = STATE_TONE[state]
                  const p = playlistOf(c.playlistId)
                  const cover = p ? mosaicOf(p, data.media)[0] : undefined
                  return (
                    <Table.Row key={c.id} id={c.id}>
                      <Table.Cell>
                        <span className="signage-table__lead">
                          <i className="signage-swatch" style={{ ['--swatch' as string]: `var(--series-${colourOf(campaigns, c.id)})` } as CSSProperties} aria-hidden="true" />
                          {cover ? <Thumb media={cover} className="signage-thumb" /> : <span className="signage-thumb"><Icon name="play" size={14} /></span>}
                          <span>
                            <span className="signage-table__name">{c.name}</span>
                            <span className="signage-table__sub">{p?.name ?? '—'}</span>
                          </span>
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Chip size="sm" variant={tone ? 'soft' : 'secondary'} color={tone ?? 'default'}>
                          {say(STATE_KEYS[state])}
                        </Chip>
                      </Table.Cell>
                      <Table.Cell className="signage-table__nowrap">
                        {calendarDay(c.startDate, lang)} – {calendarDay(c.endDate, lang, true)}
                      </Table.Cell>
                      <Table.Cell>
                        {campaignDayKeys(c).map((k) => say(k)).join(', ') || say('signage.everyDay')}
                        <span className="signage-table__sub">{campaignHours(c) || say('signage.allDay')}</span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="signage-table__branches" title={chosen.map((b) => b.name).join(', ') || undefined}>
                          {chosen.slice(0, ROW_BRANCHES).map((b) => b.name).join(', ') || '—'}
                          {chosen.length > ROW_BRANCHES && (
                            <Chip size="sm" variant="soft">
                              {say('signage.andMore', { n: chosen.length - ROW_BRANCHES })}
                            </Chip>
                          )}
                        </span>
                        {warned[0] && <ProblemLine name={chosen.length > 1 ? warned[0].b.name : undefined} problem={warned[0].problem} onNavigate={onNavigate} />}
                        {warned.length > 1 && <span className="signage-table__sub">{say('signage.andMoreWarn', { n: warned.length - 1 })}</span>}
                      </Table.Cell>
                      {PLACES.length > 1 ? <Table.Cell>{c.places.filter((pl) => pl in PLACE_KEYS).map((pl) => say(PLACE_KEYS[pl])).join(', ')}</Table.Cell> : null}
                      <Table.Cell>
                        {c.priority === 'high' ? (
                          <Chip size="sm" variant="primary" color="accent">
                            <Icon name="up" size={12} /> {say('signage.priority.high')}
                          </Chip>
                        ) : (
                          <span className="signage-table__sub">{say('signage.priority.normal')}</span>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      )}
      {editing && (
        <CampaignEditor
          title={editingName ?? say('signage.newCampaign')}
          form={editing.form}
          onChange={(form) => setEditing({ ...editing, form })}
          branches={branches}
          playlists={playlists}
          screens={screens}
          busy={busy}
          error={fault}
          onSave={save}
          onCancel={() => edit(null)}
          onDelete={editing.id ? () => setDeleting(true) : undefined}
          onNavigate={onNavigate}
        />
      )}
      {deleting && editing?.id && (
        <ConfirmModal
          title={say('signage.deleteOne', { name: editingName ?? '' })}
          confirm={say('common.delete')}
          busy={busy}
          onClose={() => setDeleting(false)}
          onConfirm={() => void run(() => api.deleteCampaign(editing.id!), 'signage.deleted')}
        />
      )}
    </section>
  )
}

/** One week at one branch: each campaign's hours as a block, overlaps side by side. A click on a day asks what plays then. */
function WeekView({ campaigns, branches, playlists, onEdit }: { campaigns: Campaign[]; branches: BranchSeat[]; playlists: PlaylistSummary[]; onEdit: (c: Campaign) => void }) {
  const { say, lang } = useStaffText()
  const [branchId, setBranchId] = useState('')
  const chosen = branchId || nowBranch(branches)
  const zone = branchZone(branches.find((b) => b.id === chosen)?.timezone)
  const [clock, setClock] = useState(() => wallNow(zone))
  useEffect(() => {
    setClock(wallNow(zone))
    const t = window.setInterval(() => setClock(wallNow(zone)), 60_000)
    return () => window.clearInterval(t)
  }, [zone])
  const [monday, setMonday] = useState(() => weekStartOf(clock.day))
  const [moment, setMoment] = useState<{ day: string; minute: number } | null>(null)
  const days = useMemo(() => weekOf(campaigns, monday, chosen, 'board', hasItems(playlists)), [campaigns, monday, chosen, playlists])
  const [fromH, toH] = hourRange(days)
  const span = (toH - fromH) * 60
  const at = (min: number) => `${((Math.min(Math.max(min, fromH * 60), toH * 60) - fromH * 60) / span) * 100}%`
  const hours = Array.from({ length: toH - fromH }, (_, i) => fromH + i)
  const thisWeek = monday === weekStartOf(clock.day)
  const label = `${calendarDay(monday, lang)} – ${calendarDay(addDays(monday, 6), lang, true)}`

  if (branches.length === 0) return <Empty icon="pin" title={say('common.noBranchesYet')} />
  return (
    <div className="signage-week">
      <div className="signage-week__bar">
        <PickOne hideLabel className="signage-week__branch" label={say('signage.branch')} value={chosen} onChange={setBranchId} options={branches.map((b) => ({ value: b.id, label: b.name }))} />
        <span className="signage-week__nav">
          <Button isIconOnly size="sm" variant="ghost" aria-label={say('signage.prevWeek')} onPress={() => setMonday(addDays(monday, -7))}>
            <Icon name="left" size={16} />
          </Button>
          <Button size="sm" variant="secondary" isDisabled={thisWeek} onPress={() => setMonday(weekStartOf(clock.day))}>
            {say('signage.thisWeek')}
          </Button>
          <Button isIconOnly size="sm" variant="ghost" aria-label={say('signage.nextWeek')} onPress={() => setMonday(addDays(monday, 7))}>
            <Icon name="right" size={16} />
          </Button>
        </span>
        <strong className="signage-week__label">{label}</strong>
        <span className="signage-bar__gap" />
        <span className="signage-week__legend">
          <span>
            <i className="signage-legend signage-legend--high" aria-hidden="true" /> {say('signage.priority.high')}
          </span>
          <span>
            <i className="signage-legend signage-legend--outranked" aria-hidden="true" /> {say('signage.state.outranked')}
          </span>
          <span>
            <Icon name="swap" size={12} /> {say('signage.shared')}
          </span>
        </span>
      </div>
      <div className="signage-week__grid" style={{ ['--rows' as string]: toH - fromH } as CSSProperties}>
        <div className="signage-week__corner" />
        {days.map((d) => (
          <div key={d.date} className="signage-week__day" data-today={d.date === clock.day || undefined}>
            <span>{say(`designs.media.day.${d.weekday}` as StaffKey)}</span>
            <strong>{Number(d.date.slice(8, 10))}</strong>
          </div>
        ))}
        <div className="signage-week__hours" aria-hidden="true">
          {hours.map((h) => (
            <span key={h}>{clockOfMinutes(h * 60)}</span>
          ))}
        </div>
        {days.map((d) => (
          <div
            key={d.date}
            className="signage-week__col"
            data-today={d.date === clock.day || undefined}
            role="group"
            aria-label={calendarDay(d.date, lang)}
            onClick={(e) => {
              if ((e.target as HTMLElement).closest('.signage-block')) return
              const r = e.currentTarget.getBoundingClientRect()
              const minute = Math.floor((fromH * 60 + ((e.clientY - r.top) / r.height) * span) / 15) * 15
              setMoment({ day: d.date, minute: Math.min(Math.max(minute, 0), 1439) })
            }}
          >
            {d.blocks.map((b) => (
              <WeekBlock key={b.campaign.id} block={b} colour={colourOf(campaigns, b.campaign.id)} top={at(b.from)} height={`calc(${at(b.to)} - ${at(b.from)})`} span={[b.from, b.to]} onEdit={() => onEdit(b.campaign)} />
            ))}
            {d.date === clock.day && clock.minute >= fromH * 60 && clock.minute <= toH * 60 && <i className="signage-week__now" style={{ top: at(clock.minute) }} aria-hidden="true" />}
            {moment?.day === d.date && <i className="signage-week__pin" style={{ top: at(moment.minute) }} aria-hidden="true" />}
          </div>
        ))}
      </div>
      {moment ? (
        <MomentAnswer branchId={chosen} zone={zone} moment={moment} onClear={() => setMoment(null)} />
      ) : (
        <p className="signage-muted signage-week__hint">{say('signage.clickToCheck')}</p>
      )}
    </div>
  )
}

function WeekBlock({ block: b, colour, top, height, span, onEdit }: { block: Block; colour: number; top: string; height: string; span: [number, number]; onEdit: () => void }) {
  const { say } = useStaffText()
  const allDay = span[0] === 0 && span[1] === 1440
  const hours = allDay ? say('signage.allDay') : `${clockOfMinutes(span[0])}–${clockOfMinutes(span[1])}`
  const len = span[1] - span[0]
  const style = {
    top,
    height,
    left: `calc(${(b.lane / b.lanes) * 100}% + 2px)`,
    width: `calc(${100 / b.lanes}% - 4px)`,
    ['--block' as string]: `var(--series-${colour})`,
  } as CSSProperties
  const notes = [b.high ? say('signage.priority.high') : '', b.outranked.length > 0 ? say('signage.state.outranked') : '', b.shared ? say('signage.shared') : '', b.empty ? say('signage.state.empty') : '']
    .filter(Boolean)
    .join(' · ')
  return (
    <button
      type="button"
      className="signage-block"
      data-high={b.high || undefined}
      data-empty={b.empty || undefined}
      style={style}
      title={`${b.campaign.name} · ${hours}${notes ? ` · ${notes}` : ''}`}
      aria-label={`${b.campaign.name}, ${hours}${notes ? `, ${notes}` : ''}`}
      onClick={onEdit}
    >
      {b.outranked.map(([from, to]) => (
        <i key={from} className="signage-block__outranked" style={{ top: `${((from - span[0]) / len) * 100}%`, height: `${((to - from) / len) * 100}%` }} aria-hidden="true" />
      ))}
      <span className="signage-block__name">
        {b.high && <Icon name="up" size={11} />}
        {b.shared && <Icon name="swap" size={11} />}
        {b.campaign.name}
      </span>
      <span className="signage-block__time">{hours}</span>
    </button>
  )
}

/** What the server says plays at a branch at a chosen moment of the week. */
function MomentAnswer({ branchId, zone, moment, onClear }: { branchId: string; zone: string; moment: { day: string; minute: number }; onClear: () => void }) {
  const { say, lang } = useStaffText()
  const at = `${moment.day}T${clockOfMinutes(moment.minute)}`
  const [now, setNow] = useState<SignageNow | null>(null)
  const [fault, setFault] = useState('')
  useEffect(() => {
    let live = true
    setNow(null)
    setFault('')
    api
      .signageNow(branchId, 'board', at)
      .then((n) => live && setNow(n))
      .catch((err) => live && setFault(explain(err, lang, 'common.unreachable')))
    return () => {
      live = false
    }
  }, [branchId, at, lang])
  const source = now && (now.campaigns.length > 0 ? now.campaigns.map((c) => c.name).join(', ') : now.items.length > 0 ? (now.design ?? say('signage.now.design')) : say('signage.now.nothing'))
  return (
    <div className="signage-at" role="status">
      <span className="signage-at__when">
        <Icon name="clock" size={14} /> {calendarDay(moment.day, lang)} · {clockOfMinutes(moment.minute)}
      </span>
      {fault ? (
        <span className="signage-warn">{fault}</span>
      ) : !now ? (
        <span className="signage-muted">{say('common.loading')}</span>
      ) : (
        <>
          <strong className="signage-at__what">{source}</strong>
          {now.changesAt && <span className="signage-muted">{say('signage.now.until', { time: changeMoment(now.changesAt, new Date(`${at}:00Z`), lang, zone) })}</span>}
          <span className="signage-at__thumbs">
            {now.items.slice(0, 6).map((item, i) => (
              <Thumb key={`${i}:${item.assetId}`} media={item} className="signage-thumb" />
            ))}
          </span>
        </>
      )}
      <span className="signage-bar__gap" />
      <Button isIconOnly size="sm" variant="ghost" aria-label={say('common.clear')} onPress={onClear}>
        <Icon name="close" size={14} />
      </Button>
    </div>
  )
}

function CampaignEditor({
  title,
  form,
  onChange,
  branches,
  playlists,
  screens,
  busy,
  error,
  onSave,
  onCancel,
  onDelete,
  onNavigate,
}: {
  title: string
  form: CampaignForm
  onChange: (form: CampaignForm) => void
  branches: BranchSeat[]
  playlists: PlaylistSummary[]
  screens: SignageScreen[] | null
  busy: boolean
  error: string
  onSave: () => void
  onCancel: () => void
  onDelete?: () => void
  onNavigate: (to: string) => void
}) {
  const { say } = useStaffText()
  const set = (patch: Partial<CampaignForm>) => onChange({ ...form, ...patch })
  const warned = branches
    .filter((b) => form.branchIds.includes(b.id))
    .map((b) => ({ b, problem: branchProblem(b, screens) }))
    .filter((x): x is { b: BranchSeat; problem: BranchProblem } => !!x.problem)
  return (
    <SignageDrawer
      title={title}
      onClose={onCancel}
      className="signage-drawer--form"
      foot={
        <>
          {onDelete && (
            <Button variant="danger-soft" className="signage-drawer__danger" isDisabled={busy} onPress={onDelete}>
              <Icon name="trash" size={15} /> {say('common.delete')}
            </Button>
          )}
          <Button variant="tertiary" onPress={onCancel}>
            {say('common.cancel')}
          </Button>
          <Button variant="primary" isPending={busy} onPress={onSave}>
            {busy ? say('common.saving') : say('common.save')}
          </Button>
        </>
      }
    >
      <div className="signage-form">
        {error && <Notice>{error}</Notice>}
        <TextField className="signage-field" value={form.name} maxLength={80} onChange={(name) => set({ name })}>
          <Label>{say('signage.name')}</Label>
          <Input variant="secondary" />
        </TextField>
        <PickOne label={say('signage.playlist')} value={form.playlistId} onChange={(playlistId) => set({ playlistId })} options={playlists.map((p) => ({ value: p.id, label: p.name }))} />
        <div className="signage-form__group">
          <span className="signage-form__heading">{say('signage.schedule')}</span>
          <div className="signage-form__pair">
            <DayField label={say('signage.from')} value={form.startDate} onChange={(startDate) => set({ startDate })} />
            <DayField label={say('signage.to')} value={form.endDate} min={form.startDate} onChange={(endDate) => set({ endDate })} />
          </div>
          <div className="signage-field">
            <span className="signage-field__label">
              {say('signage.days')}
              {form.weekdays.length === 0 && <span className="signage-muted"> · {say('signage.everyDay')}</span>}
            </span>
            <ToggleButtonGroup
              className="signage-days"
              size="sm"
              selectionMode="multiple"
              aria-label={say('signage.days')}
              selectedKeys={form.weekdays}
              onSelectionChange={(keys) => set({ weekdays: DAYS.filter((d) => keys.has(d)) })}
            >
              {DAYS.map((d) => (
                <ToggleButton key={d} id={d}>
                  {say(`designs.media.day.${d}` as StaffKey)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>
          <Switch className="signage-switch" isSelected={form.allDay} onChange={(allDay) => set({ allDay })}>
            <Switch.Content className="signage-switch__hit">
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Label>{say('signage.allDay')}</Label>
            </Switch.Content>
          </Switch>
          {!form.allDay && (
            <div className="signage-form__pair">
              <ClockField label={say('designs.media.from')} value={form.from} onChange={(from) => set({ from })} />
              <ClockField label={say('designs.media.to')} value={form.to} onChange={(to) => set({ to })} />
            </div>
          )}
        </div>
        <div className="signage-form__group">
          <span className="signage-form__heading">{say('signage.where')}</span>
          <div className="signage-field">
            <span className="signage-field__label">{say('signage.branches')}</span>
            <MultiSelect
              options={branches.map((b) => ({ value: b.id, label: b.name, note: branchNote(b) ? say(branchNote(b)!) : undefined }))}
              value={form.branchIds}
              onChange={(branchIds) => set({ branchIds })}
              placeholder={say('signage.addBranches')}
              searchPlaceholder={say('signage.findBranch')}
            />
            {warned.map(({ b, problem }) => (
              <ProblemLine key={b.id} name={b.name} problem={problem} onNavigate={onNavigate} />
            ))}
          </div>
          {PLACES.length > 1 && (
            <div className="signage-field">
              <span className="signage-field__label">{say('signage.places')}</span>
              <ToggleButtonGroup
                className="signage-days"
                size="sm"
                selectionMode="multiple"
                aria-label={say('signage.places')}
                selectedKeys={form.places}
                onSelectionChange={(keys) => set({ places: PLACES.filter((p) => keys.has(p)) })}
              >
                {PLACES.map((p) => (
                  <ToggleButton key={p} id={p}>
                    {say(PLACE_KEYS[p])}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </div>
          )}
          <div className="signage-field">
            <span className="signage-field__label">{say('signage.priority')}</span>
            <ToggleButtonGroup
              className="signage-seg"
              size="sm"
              selectionMode="single"
              disallowEmptySelection
              aria-label={say('signage.priority')}
              selectedKeys={[form.priority]}
              onSelectionChange={(keys) => {
                const next = [...keys][0]
                if (next === 'normal' || next === 'high') set({ priority: next })
              }}
            >
              {(['normal', 'high'] as const).map((p) => (
                <ToggleButton key={p} id={p}>
                  {say(p === 'high' ? 'signage.priority.high' : 'signage.priority.normal')}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>
        </div>
        <Switch className="signage-switch" isSelected={form.active} onChange={(active) => set({ active })}>
          <Switch.Content className="signage-switch__hit">
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
            <Label>{say('signage.on')}</Label>
          </Switch.Content>
        </Switch>
      </div>
    </SignageDrawer>
  )
}

